/**
 * uiZoom — a pidly-native "interface zoom", the in-app equivalent of the
 * browser's Ctrl +/- so users don't have to leave pidly to size the UI.
 *
 * Publishes the chosen factor as the CSS variable `--pidly-ui-zoom` on <html>.
 * The page CSS consumes it with the SAME zoom + size-compensation pattern
 * ProjectListPage already proves works (see ProjectWorkspace.css):
 *     zoom: var(--pidly-ui-zoom);
 *     width:  calc(100vw / var(--pidly-ui-zoom));
 *     height: calc(100vh / var(--pidly-ui-zoom));
 * The compensation is REQUIRED — `zoom` shrinks the layout box but doesn't
 * reflow viewport units, so a plain `zoom` on a 100vw/100vh element just leaves
 * a band bottom-right (the "shrink to the top-left" bug). Sizing to 1/zoom
 * makes the post-zoom render land at exactly viewport size.
 *
 * Applied to the CHROME only — file sidebar, side panels, left rail, floating
 * dialogs. The PDF canvas is deliberately left at 1: CSS-zooming it fights the
 * app's pixel-based pan/zoom/drawing maths and softens the render. So this is
 * a browser-style zoom of the interface that leaves the drawing area alone.
 *
 * Coordinate math elsewhere is ratio-based (getBoundingClientRect fractions),
 * which is invariant under zoom, so nothing downstream needs to know about this
 * — except code that counter-scales portalled popouts, which must read
 * getChromeZoom() (it folds this preference in).
 *
 * Persisted per-browser. Default 1.0 → no change for anyone who never touches it.
 */

const KEY = 'pidly.uiZoom';
// The number the USER sees: 100% is normal, 80-110% the range.
export const UI_ZOOM_MIN = 0.8;
export const UI_ZOOM_MAX = 1.1;
export const UI_ZOOM_STEP = 0.05;

// What the user's 100% actually RENDERS at. The chrome at its original size
// was bigger than it needed to be — what used to be "90%" is the sensible
// normal — so the displayed scale is rebased onto it. Keeping this separate
// from the user-facing number means the dialog still reads a clean 100% for
// normal, while the CSS gets 0.9.
export const UI_ZOOM_BASE = 0.9;
export const UI_ZOOM_DEFAULT = 1;

const clamp = (z) => Math.min(UI_ZOOM_MAX, Math.max(UI_ZOOM_MIN, z));
// Snap to the step grid + fix float drift (0.7000000000000001 → 0.7).
const snap = (z) => Math.round(clamp(z) * 100) / 100;

export function getUiZoom() {
  try {
    const raw = parseFloat(localStorage.getItem(KEY));
    return Number.isFinite(raw) ? snap(raw) : UI_ZOOM_DEFAULT;
  } catch { return UI_ZOOM_DEFAULT; }
}

// Publish the factor as --pidly-ui-zoom on <html>. Called at boot (main.jsx)
// and on every change. The CSS does the actual scaling + compensation.
/**
 * The factor actually rendered = the user's number x UI_ZOOM_BASE.
 *
 * Everything that scales chrome or counter-scales a popout must use THIS, not
 * getUiZoom() — the two differ by the base and mixing them mis-places popouts.
 */
export function getEffectiveUiZoom(z = getUiZoom()) {
  return Math.round(z * UI_ZOOM_BASE * wideFit() * 1000) / 1000;
}

// ─── Monitor fit: laptop fit's opposite ──────────────────────────────────────
// On a big monitor at the browser's 100% the chrome was too small: the user's
// 110% looked right only with the browser at 125% as well (2026-11-04, a
// 2560-wide monitor). A window wider than WIDE_BREAKPOINT CSS px scales the
// chrome AND the dialogs by CHROME_ZOOM_WIDE, folded into --pidly-ui-zoom
// (so every rule that reads it follows) and --dialog-scale. It is a class on
// <html>, set beside the laptop-fit one by installLaptopFit; the two never
// both apply.
export const WIDE_FIT_CLASS = 'pidly-wide-fit';
export const WIDE_BREAKPOINT = 2200;
export const CHROME_ZOOM_WIDE = 1.25;
export function shouldUseWideFit() {
  return typeof window !== 'undefined' && window.innerWidth > WIDE_BREAKPOINT;
}
function wideFit() {
  try { return document.documentElement.classList.contains(WIDE_FIT_CLASS) ? CHROME_ZOOM_WIDE : 1; } catch { return 1; }
}

export function applyUiZoom(z = getUiZoom()) {
  const el = typeof document !== 'undefined' && document.documentElement;
  if (!el) return;
  const eff = getEffectiveUiZoom(z);
  // Always set it — the user's 100% is an effective 0.9, so there's no value
  // at which removing the variable is correct any more.
  if (eff === 1) el.style.removeProperty('--pidly-ui-zoom');
  else el.style.setProperty('--pidly-ui-zoom', String(eff));
}

// Set + persist + apply + notify subscribers. Returns the applied value.
export function setUiZoom(z) {
  const next = snap(z);
  try { localStorage.setItem(KEY, String(next)); } catch { /* quota */ }
  applyUiZoom(next);
  try { window.dispatchEvent(new CustomEvent('pidly:ui-zoom-changed', { detail: next })); } catch { /* no-op */ }
  // The chrome scale is (laptop fit x user preference), so changing the
  // preference changes it. Anything that positions a portalled popout has
  // measured against the old number and must re-measure.
  try { window.dispatchEvent(new CustomEvent(CHROME_ZOOM_EVENT, { detail: getChromeZoom() })); } catch { /* no-op */ }
  return next;
}

export function stepUiZoom(dir) {
  return setUiZoom(getUiZoom() + dir * UI_ZOOM_STEP);
}
export function resetUiZoom() {
  return setUiZoom(UI_ZOOM_DEFAULT);
}

// Subscribe to changes (e.g. so a control's label updates when the keyboard
// shortcut fires). Returns an unsubscribe function.
export function onUiZoomChange(cb) {
  const handler = (e) => cb(typeof e.detail === 'number' ? e.detail : getUiZoom());
  window.addEventListener('pidly:ui-zoom-changed', handler);
  return () => window.removeEventListener('pidly:ui-zoom-changed', handler);
}

// ─── Laptop fit: the automatic chrome zoom ───────────────────────────────────
// Separate from the user preference above. On small windows the chrome
// (toolbars, side panels, file sidebar, rail) shrinks to 80% so it hands width
// to the drawing; the PDF canvas is deliberately NOT zoomed so its own zoom
// stays pixel-accurate. Driven by a class on <html> that the CSS keys off.
//
// This block exists because the class check and the 1600px threshold were
// copy-pasted across eleven files — the check as
// `classList.contains('pidly-mainview-zoom') ? 0.8 : 1` in 16 places, the
// threshold in another 6. Popouts that portal to <body> land OUTSIDE the zoomed
// subtree and have to counter-scale themselves, which is what all those call
// sites are doing. With the scale hardcoded in every one of them, it could only
// ever be 0.8 or 1, so the user preference above could never reach the chrome.

/** <html> class the CSS `zoom` rules key off. */
export const CHROME_ZOOM_CLASS = 'pidly-mainview-zoom';
/** Window width at or below which the chrome shrinks to fit a laptop screen. */
export const CHROME_ZOOM_BREAKPOINT = 1600;
/** Scale applied while the laptop-fit class is on. */
export const CHROME_ZOOM_SMALL = 0.8;
/** Fired when the chrome scale changes, so listeners can re-measure. */
export const CHROME_ZOOM_EVENT = 'pidly:chrome-zoom-changed';

/** True when the viewport is small enough to warrant the shrunken chrome. */
export function shouldUseLaptopFit() {
  if (typeof window === 'undefined') return false;
  // A PHONE is not a small laptop: its layout (components/MainView/Phone,
  // the phone rules in ProjectWorkspace.css) is drawn for it at 1:1. Fit used
  // to switch on for every phone too and shrank it all to 0.72.
  return window.innerWidth > 768 && window.innerWidth <= CHROME_ZOOM_BREAKPOINT;
}

/**
 * The scale the CHROME is currently rendering at.
 *
 * Read this — never `classList.contains(...) ? 0.8 : 1` — anywhere that needs
 * to counter-scale a portalled popout or convert between zoomed and unzoomed
 * pixels. Safe before mount / during SSR (returns 1).
 *
 * Now the PRODUCT of laptop-fit and the user's interface zoom, matching what
 * the CSS renders: the laptop rules are `calc(0.8 * var(--pidly-ui-zoom, 1))`
 * and the base rules `var(--pidly-ui-zoom, 1)`. These two have to agree
 * exactly — JS reporting a different number than the CSS renders mis-places
 * every portalled popout, which is why the preference was left unapplied until
 * both could change together.
 */
export function getChromeZoom() {
  try {
    if (typeof document === 'undefined') return 1;
    const laptop = document.documentElement.classList.contains(CHROME_ZOOM_CLASS) ? CHROME_ZOOM_SMALL : 1;
    // The EFFECTIVE zoom (user number x base) — that's what the CSS renders.
    // Rounded to kill float drift (0.8 x 1.1 = 0.8800000000000001).
    return Math.round(laptop * getEffectiveUiZoom() * 1000) / 1000;
  } catch {
    return 1;
  }
}

/**
 * Apply the laptop-fit class for the current viewport and keep it in step with
 * resizes. Call from a page that owns the chrome (MainView, Classes,
 * Vectorisation). Returns a cleanup that also REMOVES the class, so pages which
 * don't opt in are unaffected — use it as a useEffect return value.
 */
export function installLaptopFit() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  const apply = () => {
    const el = document.documentElement;
    const next = shouldUseLaptopFit();
    const wide = !next && shouldUseWideFit();
    // No event storm per resize tick: only a change does anything.
    if (el.classList.contains(CHROME_ZOOM_CLASS) === next && el.classList.contains(WIDE_FIT_CLASS) === wide) return;
    el.classList.toggle(CHROME_ZOOM_CLASS, next);
    el.classList.toggle(WIDE_FIT_CLASS, wide);
    applyUiZoom();
    try { window.dispatchEvent(new CustomEvent(CHROME_ZOOM_EVENT, { detail: getChromeZoom() })); } catch { /* no-op */ }
  };
  apply();
  window.addEventListener('resize', apply);
  return () => {
    window.removeEventListener('resize', apply);
    document.documentElement.classList.remove(CHROME_ZOOM_CLASS);
    document.documentElement.classList.remove(WIDE_FIT_CLASS);
    applyUiZoom();
    try { window.dispatchEvent(new CustomEvent(CHROME_ZOOM_EVENT, { detail: 1 })); } catch { /* no-op */ }
  };
}

/** Subscribe to chrome-scale changes. Returns an unsubscribe function. */
export function onChromeZoomChange(cb) {
  const handler = (e) => cb(typeof e.detail === 'number' ? e.detail : getChromeZoom());
  window.addEventListener(CHROME_ZOOM_EVENT, handler);
  return () => window.removeEventListener(CHROME_ZOOM_EVENT, handler);
}

/**
 * THE DIALOG SCALE: the size every dialog, menu and pop-out renders at,
 * taken from the SCREEN only — 1 on a desktop monitor, CHROME_ZOOM_SMALL on
 * a laptop (laptop fit on). Never the interface zoom setting: that resizes
 * the chrome (panels, rails, toolbars), and dialogs that followed it came
 * out at a different size depending on where they were opened from (the
 * 2026-10-07 dialog audit). The CSS renders the same number as
 * `--dialog-scale` (pages/ProjectWorkspace.css "Dialogs: one scale").
 */
export function getDialogScale() {
  try {
    if (typeof document === 'undefined') return 1;
    const el = document.documentElement;
    return el.classList.contains(CHROME_ZOOM_CLASS) ? CHROME_ZOOM_SMALL
      : el.classList.contains(WIDE_FIT_CLASS) ? CHROME_ZOOM_WIDE : 1;
  } catch {
    return 1;
  }
}

/**
 * The scale OVERLAYS drawn outside the zoomed chrome render at (menus,
 * pickers, hover cards, dialogs): the dialog scale. `--chrome-fit` in the
 * CSS is the same number; JS that sizes or places a scaled overlay from a
 * measured rect reads this.
 */
export function getOverlayFit() {
  return getDialogScale();
}

/**
 * Inline style that scales a point-placed overlay by the laptop fit (see
 * getOverlayFit), grown from the corner it is anchored by, so the edge that
 * touches its panel or anchor stays put. `{}` at desktop sizes. `scale` is a
 * string: React would append px to a number.
 */
export function fitPopStyle(originX = 'left', originY = 'top') {
  const f = getOverlayFit();
  return f === 1 ? {} : { scale: String(f), transformOrigin: `${originX} ${originY}` };
}
